import { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from './Layout';

const pad = (n) => String(n).padStart(2, '0');

export const forInput = (value) => {
  if (!value) return '';

  const at = new Date(value);

  if (Number.isNaN(at.getTime())) return '';

  return `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}T${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
};

export const fromInput = (value) => {
  if (!value) return '';

  const at = new Date(`${value}Z`);

  return Number.isNaN(at.getTime()) ? '' : at.toISOString();
};

export const overridesFrom = (dashboard) => Object.entries(dashboard || {})
  .filter(([, placement]) => placement)
  .reduce((all, [code, placement]) => ({
    ...all,
    [code]: { starts_at: placement.starts_at || '', ends_at: placement.ends_at || '' },
  }), {});

export function useEditorForm({ record, blank, mutation, onClose, savedMessage }) {
  const [values, setValues] = useState({
    ...blank,
    ...record,
    app_codes: record.app_codes || [],
    overrides: overridesFrom(record.app_dashboard),
  });
  const [errors, setErrors] = useState({});
  const [confirmingExit, setConfirmingExit] = useState(false);
  const setToast = useToast();

  const opened = useRef(values);

  const setField = (field) => (value) => {
    setValues((current) => ({ ...current, [field]: value }));
    const reported = ['app_codes', 'overrides'].includes(field) ? 'app_dashboard' : field;
    setErrors(({ [reported]: cleared, ...rest }) => rest);
  };
  const dirty = useMemo(() => {
    const { image, ...rest } = values;
    const { image: wasImage, ...before } = opened.current;

    return Boolean(image) !== Boolean(wasImage) || JSON.stringify(rest) !== JSON.stringify(before);
  }, [values]);

  useEffect(() => {
    if (!dirty) return undefined;

    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const discard = () => setValues(opened.current);
  const requestClose = () => (dirty ? setConfirmingExit(true) : onClose());

  const submit = () => {
    setErrors({});
    mutation.mutate(
      record.id ? { ...values, id: record.id } : values,
      {
        onSuccess: () => onClose(savedMessage),
        onError: (error) => {
          const byField = error?.response?.data?.errors || {};

          setErrors(byField);
          const fields = Object.keys(byField).filter((key) => key !== 'base');
          const base = byField.base || [];

          if (!fields.length) {
            setToast(base.length ? base.join(' ') : 'Could not save. Please try again.');

            return;
          }

          const messages = fields.flatMap((key) => byField[key]);

          setToast(messages.length === 1
            ? `Not saved. ${messages[0]}.`
            : 'Not saved. Fix the fields marked in red.');

          window.setTimeout(() => {
            const first = document.querySelector('[class*="InlineError"], [class*="Text--critical"]');

            if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 80);
        },
      }
    );
  };

  const errorFor = (field) => (errors[field] ? errors[field].join(', ') : undefined);

  return {
    confirmingExit,
    dirty,
    discard,
    errorFor,
    errors,
    keepEditing: () => setConfirmingExit(false),
    leaveAnyway: () => {
      setConfirmingExit(false);
      onClose();
    },
    requestClose,
    setField,
    submit,
    values,
  };
}
